// Create a new custom element for product links with images for transitions to PDP
class ProductCardLink extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (event) =>
      setTimeout(() => this.#handleClick(event), 0),
    );
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#handleClick);
  }

  get productTransitionEnabled() {
    return this.getAttribute("data-product-transition") === "true";
  }

  get featuredMediaUrl() {
    return this.getAttribute("data-featured-media-url");
  }

  /**
   * Active Intelligems A/B media group from the document root.
   * @returns {"control" | "variant" | null}
   */
  get #abMediaGroup() {
    const group = document.documentElement.getAttribute("data-ab-media-group");
    if (group === "control" || group === "variant") return group;
    return null;
  }

  /**
   * Handles the click event for the product link
   * @param {Event} event
   */
  #handleClick = (event) => {
    // If the event has been prevented, don't do anything, another component is handling the click or if it's an input (swatches)
    if (event.defaultPrevented || event.target instanceof HTMLInputElement)
      return;

    const gallery = this.querySelector(
      "[data-view-transition-to-main-product]",
    );
    if (!this.productTransitionEnabled || !(gallery instanceof HTMLElement))
      return;

    const activeImage = this.#getActiveImage(gallery);

    if (activeImage instanceof HTMLImageElement)
      this.#setImageSrcset(activeImage);

    gallery.setAttribute(
      "data-view-transition-type",
      "product-image-transition",
    );
    gallery.setAttribute("data-view-transition-triggered", "true");
  };

  /**
   * Resolves the image that should participate in the product view transition.
   * Prefers the visible A/B group image when present so `:last-child` cannot
   * pick a hidden control/variant sibling.
   * @param {HTMLElement} gallery
   * @returns {Element | null}
   */
  #getActiveImage(gallery) {
    const slideshowImage = gallery.querySelector(
      'slideshow-slide[aria-hidden="false"] [transitionToProduct="true"]',
    );
    if (slideshowImage) return slideshowImage;

    const abMediaRoot = gallery.querySelector("[data-ab-media]");
    // Prefer the group this root actually resolved to. A root with no media in the
    // requested group falls back to control, and ab-media.js records that on the root
    // itself — reading only `<html>` would look for a variant image that isn't there.
    const abGroup =
      abMediaRoot?.getAttribute("data-ab-media-group") || this.#abMediaGroup;
    if (abMediaRoot && abGroup) {
      // Resource cards put data-ab-group on the img; PLP cards put it on the wrapper.
      const abImage =
        abMediaRoot.querySelector(
          `[data-ab-group="${abGroup}"]:not([hidden])[transitionToProduct="true"]`,
        ) ||
        abMediaRoot.querySelector(
          `[data-ab-group="${abGroup}"]:not([hidden]) [transitionToProduct="true"]`,
        );
      if (abImage) return abImage;
    }

    return gallery.querySelector('[transitionToProduct="true"]:last-child');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    // A/B variant cards already show the treatment image. Rewriting to
    // data-featured-media-url (always the Liquid control featured URL) flashes
    // the old image into the view-transition snapshot. Control / untagged cards
    // keep the Horizon reset-to-featured behavior for swatch/hover previews.
    //
    // Keyed off THIS image's group only. Also bailing on `#abMediaGroup === 'variant'`
    // would suppress the reset for every untagged card on the page whenever the
    // visitor happens to be in the variant arm, since those images have no group.
    const imageGroup = image
      .closest("[data-ab-group]")
      ?.getAttribute("data-ab-group");
    if (imageGroup === "variant") return;

    if (
      this.featuredMediaUrl &&
      !image.srcset.includes(this.featuredMediaUrl)
    ) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: "ease-in-out",
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? "";
      };
    }
  }
}

if (!customElements.get("product-card-link")) {
  customElements.define("product-card-link", ProductCardLink);
}
